import {
  createContext,
  ReactNode,
  FC,
  useState,
  useMemo,
  useEffect,
} from "react";
import { Contact } from "../../types/Contact";
import { DocumentLayout } from "../../types/DocumentLayout";
import { Profile } from "../../types/Profile";
import {
  fetchBankAccount,
  fetchDocumentLayout,
  fetchProfile,
  fetchProfileContact,
} from "../restapi/profileService";
import { fetchContact } from "../restapi/contactService";
import { BankAccount } from "../../types/BankAccount";

interface ResourceContextInterface {
  profile: Profile | undefined;
  contact: Contact | undefined;
  customerContact: Contact | undefined;
  documentLayout: DocumentLayout | undefined;
  bankAccount: BankAccount | undefined;
  loading: boolean;
  partnerIncluded: boolean;
  setCustomerContact: (val: Contact) => void;
}

export const ResourceContext = createContext({} as ResourceContextInterface);

export const ResourceContextProvider: FC<{
  children: ReactNode;
  resourceId: number;
  contactId: number;
  customerContactId: number;
}> = ({ children, resourceId, contactId, customerContactId }) => {
  const [profile, setProfile] = useState<Profile>();
  const [contact, setContact] = useState<Contact>();
  const [contactLoading, setContactLoading] = useState(true);
  const [customerContact, setCustomerContact] = useState<Contact>();
  const [documentLayout, setDocumentLayout] = useState<DocumentLayout>();
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [loading, setLoading] = useState<boolean>(true);
  const partnerIncluded = customerContact !== undefined && customerContact.partner !== undefined && customerContact.partner !== null; 

  useEffect(() => {
    if (resourceId) {
      fetchDocumentLayout(resourceId).then((resp) => {
        if (resp.ok) {
          resp.json().then(setDocumentLayout);
        }
      });
      fetchProfile(resourceId).then((resp) => {
        if (resp.ok) {
          resp.json().then(setProfile);
        }
      });
      fetchBankAccount(resourceId).then((resp) => {
        if (resp.ok) {
          resp.json().then(setBankAccount);
        }
      });
    }
  }, [resourceId]);

  useEffect(() => {
    if (resourceId && customerContactId) {
      fetchContact(customerContactId, resourceId).then((resp) => {
        if (resp.ok) {
          resp.json().then(setCustomerContact);
        }
      });
    }
  }, [resourceId, customerContactId]);

  useEffect(() => {
    if (resourceId) {
      setContactLoading(true);
      const contactRequest = contactId
        ? fetchContact(contactId, resourceId)
        : fetchProfileContact(resourceId);
      contactRequest
        .then((resp) => (resp.ok ? resp.json().then(setContact) : undefined))
        .finally(() => setContactLoading(false));
    } else {
      setContactLoading(false);
    }
  }, [resourceId, contactId]);

  useEffect(() => {
    const customerContactLoaded = customerContactId ? customerContact : true;
    if (profile && !contactLoading && documentLayout && customerContactLoaded) {
      setLoading(false);
    }
  }, [profile, contactLoading, customerContact, documentLayout, customerContactId]);

  const value = useMemo(
    () => ({
      profile,
      contact,
      customerContact,
      documentLayout,
      bankAccount,
      loading,
      partnerIncluded,
      setCustomerContact,
    }),
    [profile, contact, customerContact, documentLayout, bankAccount, loading, partnerIncluded]
  );

  return (
    <ResourceContext.Provider value={value}>
      {children}
    </ResourceContext.Provider>
  );
};
